import {
  ChangeDetectorRef,
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  HostBinding,
  ViewChild,
  Inject,
  PLATFORM_ID,
  NgZone
} from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { MatSidenav } from '@angular/material/sidenav';
import { Subscription } from 'rxjs';
import { delay, filter } from 'rxjs/operators';

import { ResponsiveBreakpointsService } from '../../responsive-breakpoints.service';
import { SideMenusService } from '../../side-menus/side-menus.service';

@Component({
  selector: 'app-mobile-layout',
  templateUrl: './mobile-layout.component.html',
  styleUrls: ['./mobile-layout.component.scss']
})
export class MobileLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class.layout-mobile') layoutFlag = true;
  @HostBinding('class.server-side-rendered') serverSideRendered;
  @HostBinding('class.main-sidenav-opened') mainSidenavOpened;
  @HostBinding('class.alt-sidenav-opened') altSidenavOpened;

  @ViewChild('mainSidenav') mainSidenav: MatSidenav;
  @ViewChild('altSidenav') altSidenav: MatSidenav;

  // Subscription to the Main Sidenav movement
  mainSidenavSubscription: Subscription;
  // Subscription to the Alt Sidenav movement
  altSidenavSubscription: Subscription;
  // Subscription to the screen size changes
  screenSizeChangeSubscription: Subscription;
  // Subscription to the main side menu mode changes
  changeMainMenuModeSubscription: Subscription;
  private routeChangeSubscription?: Subscription;
  private pageShowHandler?: () => void;
  private safariShellSubscription?: Subscription;

  mainSideMenuMode: string;
  /** Driven by root `SideMenusService` + `[hidden]` so the main menu is not destroyed on navigation. */
  useMainMenuOnSafari = false;

  constructor(
    private sideMenusService: SideMenusService,
    private responsiveBreakpointsService: ResponsiveBreakpointsService,
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    this.serverSideRendered = isPlatformServer(this.platformId);

    // Always open for beta menu hover functionality
    this.mainSidenavOpened = true;
    this.mainSideMenuMode = 'side'; // Use 'side' mode so it's always visible

    if (isPlatformBrowser(this.platformId)) {
      this.sideMenusService.recomputeSafariBetaShellMainMenu(this.router.url);
      this.syncSafariShellFromService();
    }
  }

  private syncSafariShellFromService(): void {
    this.useMainMenuOnSafari = this.sideMenusService.safariBetaShellUsesMainMenu;
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.safariShellSubscription = this.sideMenusService.safariBetaShellUsesMainMenu$.subscribe((v) => {
        this.useMainMenuOnSafari = v;
        this.cdr.detectChanges();
      });

      this.routeChangeSubscription = this.router.events
        .pipe(filter((e) => e instanceof NavigationEnd))
        .subscribe(() => {
          this.ngZone.run(() => {
            this.sideMenusService.recomputeSafariBetaShellMainMenu(this.router.url);
            this.syncSafariShellFromService();
            this.cdr.detectChanges();
          });
        });

      this.pageShowHandler = () => {
        this.ngZone.run(() => {
          this.sideMenusService.recomputeSafariBetaShellMainMenu(this.router.url);
          this.syncSafariShellFromService();
          this.cdr.detectChanges();
        });
      };
      window.addEventListener('pageshow', this.pageShowHandler);
    }

    // subscribe to screen size changes
    this.screenSizeChangeSubscription = this.responsiveBreakpointsService.screenSizeChangeSubject.pipe(
      delay(0)
    ).subscribe(
      (event) => {
        if (event === 'small') {
          this.sideMenusService.changeMainMenuModeSubject.next('push');
        }
        if (event === 'medium') {
          this.sideMenusService.changeMainMenuModeSubject.next('push');
        }
        if (event === 'large') {
          this.sideMenusService.changeMainMenuModeSubject.next('side');
        }
      }
    );
  }

  ngOnDestroy() {
    this.mainSidenavSubscription?.unsubscribe();
    this.altSidenavSubscription?.unsubscribe();
    this.screenSizeChangeSubscription?.unsubscribe();
    this.changeMainMenuModeSubscription?.unsubscribe();
    this.routeChangeSubscription?.unsubscribe();
    this.safariShellSubscription?.unsubscribe();

    if (isPlatformBrowser(this.platformId) && this.pageShowHandler) {
      window.removeEventListener('pageshow', this.pageShowHandler);
      this.pageShowHandler = undefined;
    }
  }

  ngAfterViewInit(): void {
    // Ensure sidenav is always open for beta menu
    if (!this.mainSidenav.opened) {
      this.mainSidenav.open();
    }

    if (isPlatformBrowser(this.platformId)) {
      queueMicrotask(() => {
        this.ngZone.run(() => {
          this.sideMenusService.recomputeSafariBetaShellMainMenu(this.router.url);
          this.syncSafariShellFromService();
          this.cdr.detectChanges();
        });
      });
    }

    this.mainSidenavSubscription = this.sideMenusService.toggleMainMenuSubject.subscribe(
      (event) => {
        // For beta menu, always keep the menu open - ignore toggle/close commands
        if (event === 'open') {
          this.mainSidenav.open();
        }
        // Ignore 'toggle' and 'close' to keep menu always visible
      },
      (error) => {
        console.log('toggleMainMenu [Layout Mobile] - error', error);
      },
      () => {
        console.log('toggleMainMenu [Layout Mobile] - complete');
      }
    );

    this.altSidenavSubscription = this.sideMenusService.toggleAltMenuSubject.subscribe(
      (event) => {
        switch (event) {
          case 'toggle':
            this.altSidenav.toggle();
            break;
          case 'open':
            this.altSidenav.open();
            break;
          case 'close':
            this.altSidenav.close();
            break;
          default:
            this.altSidenav.toggle();
        }
      },
      (error) => {
        console.log('toggleAltMenu [Layout Mobile] - error', error);
      },
      () => {
        console.log('toggleAltMenu [Layout Mobile] - complete');
      }
    );

    this.changeMainMenuModeSubscription = this.sideMenusService.changeMainMenuModeSubject.subscribe(
      (menuMode) => {
        this.mainSideMenuMode = menuMode;
      },
      (error) => {
        console.log('changeMainMenuModeSubject [Layout Mobile] - error', error);
      },
      () => {
        console.log('changeMainMenuModeSubject [Layout Mobile] - complete');
      }
    );
  }

  // It's better to have the class applied when the opening starts as we want it ready before the opening animation ends
  openedStart(): void {
    console.log("hello it is adam");
  }

  mainSidenavToggled(opened: boolean): void {
    // Force sidenav to stay open for beta menu hover functionality
    if (!opened && this.mainSidenav) {
      setTimeout(() => {
        if (this.mainSidenav && !this.mainSidenav.opened) {
          this.mainSidenav.open();
        }
      }, 0);
    }
    this.mainSidenavOpened = true; // Always set to true
    this.sideMenusService.setMainMenuState(true);
  }

  altSidenavToggled(opened: boolean): void {
    this.altSidenavOpened = opened;
  }
}
